import React, {
  useEffect, useCallback, memo, useMemo, useState, lazy, Suspense,
} from 'react';
import Button from 'components/button';
import Time from 'components/time';
// import Note from 'components/note';
import usePreciseTimer from 'hooks/usePreciseTimer';
import useTitleActivity from 'hooks/useTitleActivity';
import useLongPress from 'hooks/useLongPress';
import CookieConsent from 'react-cookie-consent';

import {
  UPDATE_NOTE_TIMERS,
  SET_INITIAL_STATE,
  ADD_NOTE,
  REMOVE_ALL_NOTES,
  PAUSE_ALL_NOTES,
  RESTART_LAST_ACTIVE,
} from 'reducers/homePageReducer';

import {
  NOTE_ADDED,
} from 'reducers/loadingReducer';
import { useStateValue, useDispatch } from 'state/contextState';
import styled from 'styled-components';
import { lf } from 'storage/localForage';

const Note = lazy(() => import('components/note'));
// https://medium.com/welldone-software/track-redundant-re-renders-that-caused-by-hooks-with-why-did-you-render-version-3-504468deb653
// https://overreacted.io/a-complete-guide-to-useeffect/
// https://reactjs.org/docs/hooks-faq.html#is-it-safe-to-omit-functions-from-the-list-of-dependencies
// https://www.robinwieruch.de/react-hooks-fetch-data/

const Homepage = () => {
  const state = useStateValue('homePage');
  const dispatch = useDispatch();

  const memoUpdateTimers = useCallback((time) => {
    dispatch({ type: UPDATE_NOTE_TIMERS, time });
  }, [dispatch]); // Added dispatch

  useTitleActivity(state.isActive);
  usePreciseTimer(memoUpdateTimers, 1000, state.isActive);

  useEffect(() => {
    const init = async () => {
      const data = await lf.getItem('state');
      dispatch({ type: SET_INITIAL_STATE, data });
    };
    init();
  }, [dispatch]); // Added dispatch

  useEffect(() => {
    lf.setItem('state', state);
  }, [state]);

  const memoNotes = useMemo(() => state.notes.map((note) => (
    <Note
      key={note.id}
      refKey={note.id}
      seconds={note.elapsed}
      state={note.state}
      titleText={note.title}
      editorContent={note.editorContent}
    />
  )), [state.notes]);

  const memoAddNote = useCallback(() => {
    dispatch({ type: ADD_NOTE });
    dispatch({ type: NOTE_ADDED });
  }, [dispatch]); // Added dispatch

  const memoPauseAll = useCallback(() => {
    dispatch({ type: PAUSE_ALL_NOTES });
  }, [dispatch]); // Added dispatch

  const memoRestartLastActive = useCallback(() => {
    dispatch({ type: RESTART_LAST_ACTIVE });
  }, [dispatch]); // Added dispatch

  const memoCloseAll = useCallback(() => {
    dispatch({ type: REMOVE_ALL_NOTES });
  }, [dispatch]); // Added dispatch

  const [showTip, setShowTip] = useState(false);
  const {
    onClick, onMouseDown, onMouseUp, onMouseLeave: originalOnMouseLeave, onTouchStart, onTouchEnd, // Removed onMouseOver and onFocus
  } = useLongPress(memoCloseAll, 500); // Unpack pressEvents

  const memoOnMouseLeave = useCallback((e) => {
    originalOnMouseLeave(e);
    setShowTip(false);
  }, [originalOnMouseLeave]);

  const memoHandleHover = useCallback(() => {
    setShowTip(true);
  }, []);

  return (
    <div>
      <ControlPanel>
        <PanelButtons>
          <Button
            type="plus"
            title="Add"
            onClick={memoAddNote}
          />
          <Button
            type={state.isActive === 0 ? 'play' : 'pause'}
            title="Play/Pause"
            onClick={state.isActive === 0 ? memoRestartLastActive : memoPauseAll}
          />
          <Button
            type="close"
            title="Long press to delete all"
            onMouseOver={memoHandleHover}
            onFocus={memoHandleHover}
            onClick={onClick}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseLeave={memoOnMouseLeave}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          />
        </PanelButtons>
        <Banner>
          Chroniker is moving to chroniker.uk
        </Banner>
        {
          showTip && (
            <ToolTip>
              Long press to delete all
            </ToolTip>
          )
        }
        <TotalTime>
          <Time
            type="large"
            isEditable={false}
            timeSeconds={state.totalTime}
          />
        </TotalTime>
      </ControlPanel>
      <NotesPanel>
        <Suspense fallback={<LoadingText>Loading notes...</LoadingText>}>
          {memoNotes}
        </Suspense>
      </NotesPanel>
      <StyledCookieConsent>
        This website uses cookies to enhance the user experience.
      </StyledCookieConsent>
    </div>
  );
};

const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.main.fontColor};
  font-family: 'Roboto', sans-serif;
  font-weight: 300;
  text-align: center;
  flex: 1;
  margin: 0 20px;
  font-size: 1.1rem;

  @media screen and (max-width: ${(props) => props.theme.media.sm}) {
    margin: 20px 0;
    font-size: 1rem;
  }
`;

const ToolTip = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: ${(props) => props.theme.main.fontColor};
  flex-grow: 2;
  margin-left: 10px;
  font-family: 'Roboto', 'sans-serif';
  font-weight: 300;
  transition: .3s;
`;

// Styled component for "Loading notes..." text
const LoadingText = styled.div`
  font-family: Roboto, sans-serif;
  font-weight: 300;
  color: ${(props) => props.theme.main.fontColor};
  margin-left: 19px; /* Match Note component's margin-left */
  margin-bottom: 19px; /* Match Note component's margin-bottom */
`;

const StyledCookieConsent = memo(styled(CookieConsent).attrs({
  disableStyles: true,
  style: {
    alignItems: 'baseline',
    background: 'rgb(53, 53, 53)',
    color: 'white',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    left: '0px',
    position: 'fixed',
    width: '100%',
    zIndex: 999,
    bottom: '0px',
  },
  contentStyle: {
    fontFamily: 'Roboto',
    flex: '1 0 300px',
    margin: '15px',
    fontWeight: 300,
  },
  buttonStyle: {
    fontFamily: 'Roboto',
    borderRadius: '3px',
    background: 'rgb(255, 212, 45)',
    border: '0px',
    boxShadow: 'none',
    color: 'black',
    cursor: 'pointer',
    flex: '0 0 auto',
    padding: '5px 10px',
    margin: '15px',
    fontWeight: 300,
  },
})`
`);

const ControlPanel = memo(styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 50px 41px 50px 41px;
  @media screen and (max-width: ${(props) => props.theme.media.sm}){
    flex-direction: column-reverse;
    align-items: center;
   }
`);

const PanelButtons = memo(styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 150px;
  @media screen and (max-width: ${(props) => props.theme.media.sm}){
    margin-top: 50px;
   }
`);

const TotalTime = memo(styled.div`
  display: flex;
`);

const NotesPanel = memo(styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  margin-left: 12px;
  margin-right: 12px;
  @media screen and (max-width: ${(props) => props.theme.media.sm}){
    flex-direction: column;
    align-items: center;
    margin: 0 auto 20px;
   }
`);

// Homepage.whyDidYouRender = true;
export default memo(Homepage);

// change in one context key not affect other keys
